import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, Copy, ExternalLink, EyeOff, HelpCircle, Users } from 'lucide-react';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFaq, setNewFaq] = useState({ title: '', description: '' });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get('/api/faq/my');
      setFaqs(response.data);
    } catch (error) {
      console.error('Ошибка получения FAQ:', error);
      toast.error('Ошибка загрузки FAQ');
    } finally {
      setLoading(false);
    }
  };

  const createFaq = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/faq', newFaq);
      setFaqs([response.data.faq, ...faqs]);
      setNewFaq({ title: '', description: '' });
      setShowCreateModal(false);
      toast.success('FAQ успешно создан!');
    } catch (error) {
      console.error('Ошибка создания FAQ:', error);
      toast.error('Ошибка создания FAQ');
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот FAQ?')) {
      return;
    }

    try {
      await axios.delete(`/api/faq/${id}`);
      setFaqs(faqs.filter(faq => faq._id !== id));
      toast.success('FAQ удален!');
    } catch (error) {
      console.error('Ошибка удаления FAQ:', error);
      toast.error('Ошибка удаления FAQ');
    }
  };

  const copyLink = (slug) => {
    const link = `${window.location.origin}/faq/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Ссылка скопирована в буфер обмена!');
  };

  const togglePublic = async (id, isPublic) => {
    try {
      console.log('🔍 Переключаем публичность FAQ:', { id, isPublic, newValue: !isPublic });
      const response = await axios.put(`/api/faq/${id}/public`, { isPublic: !isPublic });
      console.log('✅ FAQ обновлен:', response.data);
      toast.success(`FAQ ${!isPublic ? 'опубликован' : 'скрыт'}`);
      fetchFaqs();
    } catch (error) {
      console.error('Ошибка изменения статуса:', error);
      toast.error('Ошибка изменения статуса');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-24 w-24 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Мои FAQ</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Управление</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors flex items-center space-x-2 text-sm"
        >
          <HelpCircle className="h-4 w-4" />
          <span>Создать FAQ</span>
        </button>
      </div>

      {faqs.length === 0 ? (
        <div className="text-center py-8">
          <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
            <HelpCircle className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">У вас пока нет FAQ</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Создайте ваш первый FAQ, чтобы начать работу</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors text-sm"
          >
            Создать первый FAQ
          </button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {faqs.map((faq) => (
            <div key={faq._id} className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="mb-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 line-clamp-2">
                  {faq.title}
                </h3>
              </div>

              {faq.description && (
                <p className="text-gray-600 dark:text-gray-400 text-xs mb-3 line-clamp-3">
                  {faq.description}
                </p>
              )}

              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                <span>{faq.categories?.length || 0} категорий</span>
                <div className="flex items-center space-x-2">
                  {faq.isTeamProject && (
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 flex items-center space-x-1">
                      <Users className="h-3 w-3" />
                      <span>Командный</span>
                    </span>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    faq.isPublic 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {faq.isPublic ? 'Публичный' : 'Приватный'}
                  </span>
                  <button
                    onClick={() => togglePublic(faq._id, faq.isPublic)}
                    className={`p-1 rounded-lg transition-colors ${
                      faq.isPublic 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={faq.isPublic ? 'Скрыть' : 'Опубликовать'}
                  >
                    {faq.isPublic ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <span>Создан: {new Date(faq.createdAt).toLocaleDateString('ru-RU')}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {faq.isPublic ? (
                  <a
                    href={`/faq/${faq.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition-colors text-xs flex items-center space-x-1"
                  >
                    <Eye className="h-3 w-3" />
                    <span>Публичный просмотр</span>
                  </a>
                ) : (
                  <Link
                    to={`/faq/${faq._id}/edit`}
                    className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition-colors text-xs flex items-center space-x-1"
                  >
                    <Eye className="h-3 w-3" />
                    <span>Просмотр</span>
                  </Link>
                )}
                <Link
                  to={`/faq/${faq._id}/edit`}
                  className="bg-orange-600 text-white px-3 py-1.5 rounded-md hover:bg-orange-700 transition-colors text-xs flex items-center space-x-1"
                >
                  <Edit className="h-3 w-3" />
                  <span>Редактировать</span>
                </Link>
                {faq.isPublic && (
                  <button
                    onClick={() => copyLink(faq.slug)}
                    className="bg-orange-100 text-orange-700 px-3 py-1.5 rounded-md hover:bg-orange-200 transition-colors text-xs flex items-center space-x-1"
                  >
                    <Copy className="h-3 w-3" />
                    <span>Копировать ссылку</span>
                  </button>
                )}
                <button
                  onClick={() => deleteFaq(faq._id)}
                  className="bg-red-100 text-red-600 px-3 py-1.5 rounded-md hover:bg-red-200 transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>

            </div>
          ))}
        </div>
      )}

      {/* Create FAQ Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-md p-4 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Создать новый FAQ</h2>
            <form onSubmit={createFaq}>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Название FAQ
                </label>
                <input
                  type="text"
                  value={newFaq.title}
                  onChange={(e) => setNewFaq({ ...newFaq, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder="Введите название FAQ"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Описание (необязательно)
                </label>
                <textarea
                  value={newFaq.description}
                  onChange={(e) => setNewFaq({ ...newFaq, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  rows="3"
                  placeholder="Краткое описание FAQ"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                >
                  Создать
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
